import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Injector } from '@angular/core';
import { CountriesApi, CountriesApiClient } from '@sg/api/countries-api';
import { SgEntityFetchAdapter } from '../components/entity-select-auto-complete';

declare module '../components/entity-select-auto-complete/entity-select-fetch-store.service' {
  export interface SgEntitySelectEntityFetchMap {
    Country: {
      entity: CountriesApi.CountryDto;
      query: CountriesApi.GetCountriesRequest & { name: string[] };
    };
    CountryISO2: {
      entity: CountriesApi.CountryDto;
      query: CountriesApi.GetCountriesRequest & { name: string[] };
    };
  }
}

abstract class CountryApiToEntityFetcherLinkBase {
  protected countries: CountriesApi.CountryDto[];
  protected countries$: Observable<CountriesApi.CountryDto[]>;
  protected readonly countriesApi: CountriesApiClient;

  constructor(injector: Injector) {
    this.countriesApi = injector.get(CountriesApiClient);
  }

  findEntities(query?: CountriesApi.GetCountriesRequest & { name: string[] }) {
    return this.getCountries().pipe(
      map((countries) => {
        let result = { data: countries };
        if (!!query) {
          if (query.name) {
            const names = query.name.map((n) => new RegExp(n, 'i'));
            result.data = countries.filter((c) => names.some((n) => c.name.match(n)));
          }
          if (query.iso2_codes) {
            const iso2Codes = query.iso2_codes.map((n) => new RegExp(n, 'i'));
            result.data = countries.filter((c) => iso2Codes.some((n) => c.iso2_code.match(n)));
          }
          if (query.iso3_codes) {
            const iso3Codes = query.iso3_codes.map((n) => new RegExp(n, 'i'));
            result.data = countries.filter((c) => iso3Codes.some((n) => c.is3_code.match(n)));
          }
          if (query.iso_codes) {
            result.data = countries.filter((c) => query.iso_codes.indexOf(c.iso_code) > -1);
          }
        }
        return result;
      })
    );
  }

  protected getCountries(): Observable<CountriesApi.CountryDto[]> {
    if (this.countries) {
      return of(this.countries);
    } else if (this.countries$) {
      return this.countries$;
    } else {
      return (this.countries$ = this.countriesApi
        .countriesControllerPostCountriesSearch({ body: { active: true } }, { observe: 'body' })
        .pipe(
          tap((countries) => {
            this.countries = countries;
            this.countries$ = undefined;
          })
        ));
    }
  }
}

export class CountryApiToEntityFetcherLink
  extends CountryApiToEntityFetcherLinkBase
  implements SgEntityFetchAdapter<'Country'>
{
  readonly key: 'Country' = 'Country';
  readonly metadata: {
    idField: keyof CountriesApi.CountryDto;
    labelField: keyof CountriesApi.CountryDto;
    queryField?: { field: 'name' | keyof CountriesApi.GetCountriesRequest; isArray?: boolean };
  } = {
    idField: 'iso2_code' as 'iso2_code',
    labelField: 'name' as 'name',
    queryField: { field: 'name', isArray: true },
  };

  getEntity(id: string) {
    return this.getCountries().pipe(map((countries) => countries.find((c) => c.iso2_code === id)));
  }
}

export class CountryApiISO2ToEntityFetcherLink
  extends CountryApiToEntityFetcherLinkBase
  implements SgEntityFetchAdapter<'CountryISO2'>
{
  readonly key: 'CountryISO2' = 'CountryISO2';
  readonly metadata: {
    idField: keyof CountriesApi.CountryDto;
    labelField: Array<keyof CountriesApi.CountryDto>;
    queryField?: { field: 'name' | keyof CountriesApi.GetCountriesRequest; isArray?: boolean };
  } = {
    idField: 'iso2_code' as 'iso2_code',
    labelField: ['name', 'iso2_code'],
    queryField: { field: 'iso2_codes', isArray: true },
  };

  getEntity(id: string) {
    return this.getCountries().pipe(map((countries) => countries.find((c) => c.iso2_code === id)));
  }
}
